/* إعدادات · مساحات العمل · SSH · إدارة · بحث */
import {
  $, el, S, api, apiw, toast, store, load, saveSettings, sheet, askText, confirmBox, formSheet, openModal, errMsg, emit, ic,
  clearDeviceLocalData,
} from './core.js';
import { applyPrefs, saveTab, visibleTabs, E } from './editor.js';
import { resetFiles, trashSheet, pickUpload } from './files.js';
import { loadRepos } from './git.js';
import { resetTerminals, fitActiveTerm, ensureTerminals, focusActiveTerm } from './term.js';
import { refreshAiReady } from './ai.js';
import { launchOpencode, refreshOpencode, showFreeModels, showModelStats } from './opencode.js';
import { showLivePreviewSheet } from './live-preview.js';
import { closeEvents, connectEvents } from './files.js';
import { updateExplorerProject } from './nav-shell.js';
import { openRulesEditor } from './rules-editor.js';
import { HOME_VIEW_OPTIONS } from './onboarding.js';
import { readMobileAutoSplitAgent, persistMobileAutoSplitAgent } from './mobile-ai-split.js';
import {
  appendSheetProHeader, appendSheetProSection, appendSheetProHero, appendSheetProScroll,
  sheetProRow, appendSheetProFoot, proActionSheet, proFormSheet,
} from './pro-sheet.js';

export async function switchWorkspace(id) {
  if (id === S.w) return;
  store('me.w', id);
  S.w = id;
  const wsel = $('#wsel');
  const nm = S.workspaces.find((w) => w.id === id)?.name || id;
  if (wsel) { wsel.textContent = nm; wsel.title = nm; }
  resetTerminals();
  closeEvents();
  await resetFiles();
  await loadRepos();
  connectEvents();
  updateExplorerProject();
  emitWorkspace();
  if (document.body.dataset.view === 'term' || window.innerWidth >= 900) {
    void ensureTerminals().then(() => focusActiveTerm());
  }
}

function emitWorkspace() {
  document.dispatchEvent(new CustomEvent('workspace-changed', { detail: S.w }));
}

function workspaceHint(w) {
  if (w.kind === 'ssh') {
    const live = w.connected ? 'متصل الآن · ' : '';
    return `${live}${w.host || 'SSH'} · SFTP · طرفية`;
  }
  if (w.sandbox) return 'Docker · آمن · git · طرفية';
  if (w.canExec) return 'مجلد المشروع على السيرفر · git';
  return 'قراءة وكتابة ملفات';
}

function workspaceHeroIcon(w) {
  if (w?.kind === 'ssh') return 'plug';
  if (w?.sandbox) return 'shield';
  return 'folder';
}

function workspaceSort(a, b) {
  const rank = (w) => {
    if (w.id === S.w) return 0;
    if (w.id === 'local') return 1;
    if (w.kind === 'ssh' && w.connected) return 2;
    if (w.kind === 'ssh') return 3;
    return 4;
  };
  return rank(a) - rank(b) || String(a.name).localeCompare(String(b.name), 'ar');
}

async function renameProjectFlow() {
  const cur = S.workspaces.find((w) => w.id === 'local')?.name || 'مشروع omdaa';
  const name = await askText('اسم مشروعك', cur, 'تسمية المشروع', {
    placeholder: 'مثال: متجر أحمد · تطبيق Flutter',
  });
  if (!name || name.trim() === cur) return;
  try {
    const r = await api('/prefs/project-name', { method: 'PUT', body: { name: name.trim() } });
    const local = S.workspaces.find((w) => w.id === 'local');
    if (local) local.name = r.name;
    const wsel = $('#wsel');
    if (wsel && S.w === 'local') {
      wsel.textContent = r.name;
      wsel.title = r.name;
    }
    toast('تم حفظ اسم المشروع', 'ok');
    emitWorkspace();
  } catch (e) {
    toast(errMsg(e), 'err');
  }
}

export async function workspaceSheet() {
  try {
    const me = await api('/me');
    if (me.workspaces?.length) S.workspaces = me.workspaces;
  } catch { /* قائمة محلية */ }

  const workspaces = [...S.workspaces].sort(workspaceSort);
  const hasLocal = workspaces.some((w) => w.id === 'local');
  const localName = workspaces.find((w) => w.id === 'local')?.name || 'مشروع omdaa';
  const activeWs = workspaces.find((w) => w.id === S.w) || workspaces[0];
  const lastSshId = load('me.ssh.lastConnId', '');
  const lastSsh = workspaces.find((w) => w.id === 'ssh:' + lastSshId);

  const pick = await openModal((box, done) => {
    appendSheetProHeader(box, {
      icon: 'cloud',
      title: 'مساحة العمل',
      subtitle: 'محلي · SSH · Sandbox',
      compact: true,
    });
    if (activeWs) {
      appendSheetProHero(box, {
        icon: workspaceHeroIcon(activeWs),
        kicker: 'النشط',
        title: activeWs.name,
        hint: workspaceHint(activeWs),
        compact: true,
      });
    }
    const scroll = appendSheetProScroll(box);

    appendSheetProSection(scroll, 'إجراءات سريعة', 'sparkles');
    if (lastSsh) {
      scroll.append(sheetProRow({
        icon: 'plug',
        label: 'آخر اتصال SSH',
        hint: lastSsh.name,
        badge: 'SSH',
        onclick: () => done({ id: 'last-ssh' }),
      }));
    }
    scroll.append(
      sheetProRow({
        icon: 'check',
        label: 'اختبار SSH',
        hint: 'بصمة · مفتاح · بدون 428',
        onclick: () => done({ id: 'ssh-probe' }),
      }),
      sheetProRow({
        icon: 'key',
        label: 'إدارة SSH',
        hint: 'Hosts · مفاتيح · SFTP',
        onclick: () => done({ id: 'ssh' }),
      }),
    );
    if (activeWs?.kind === 'ssh' && activeWs.connected) {
      scroll.append(sheetProRow({
        icon: 'x',
        label: 'قطع SSH الحالي',
        hint: activeWs.host || '',
        danger: true,
        onclick: () => done({ id: 'ssh-disconnect', connId: activeWs.id.slice(4) }),
      }));
    }

    if (hasLocal) {
      appendSheetProSection(scroll, 'المشروع المحلي', 'folder');
      scroll.append(sheetProRow({
        icon: 'wand',
        label: 'تسمية المشروع',
        hint: localName,
        onclick: () => done({ id: 'rename-project' }),
      }));
    }

    const sshList = workspaces.filter((w) => w.kind === 'ssh');
    const localWs = workspaces.find((w) => w.id === 'local');
    if (localWs) {
      appendSheetProSection(scroll, localWs.sandbox ? 'مشروع · Sandbox' : 'المشروع المحلي', 'server');
      for (const w of [localWs]) {
        const on = w.id === S.w;
        scroll.append(sheetProRow({
          icon: w.sandbox ? 'shield' : 'folder',
          label: w.name,
          hint: workspaceHint(w),
          badge: on ? 'نشط' : undefined,
          active: on,
          onclick: () => done({ id: w.id }),
        }));
      }
    }
    if (sshList.length) {
      appendSheetProSection(scroll, 'سيرفرات SSH', 'plug');
      for (const w of sshList) {
        const on = w.id === S.w;
        scroll.append(sheetProRow({
          icon: 'plug',
          label: w.name,
          hint: workspaceHint(w),
          badge: on ? 'نشط' : (w.connected ? 'Live' : undefined),
          active: on,
          onclick: () => done({ id: w.id }),
        }));
      }
    } else {
      appendSheetProSection(scroll, 'سيرفرات SSH', 'plug');
      scroll.append(el('p', { class: 'sheet-pro-empty', text: 'لا اتصال بعد — أضف host من «إدارة SSH».' }));
    }

    appendSheetProFoot(box, done);
  }, { cls: 'sheet-pro-glass sheet-pro-workspace' });
  if (!pick) return;
  if (pick.id === 'rename-project') renameProjectFlow();
  else if (pick.id === 'ssh') sshSheet();
  else if (pick.id === 'ssh-probe') sshProbeFlow({}, { offerSaveConnect: true });
  else if (pick.id === 'last-ssh') connectLastSsh();
  else if (pick.id === 'ssh-disconnect' && pick.connId) {
    try {
      await api('/ssh/connections/' + pick.connId + '/disconnect', { method: 'POST' });
      toast('تم قطع SSH', 'ok');
      const me = await api('/me');
      if (me.workspaces?.length) S.workspaces = me.workspaces;
    } catch (e) { toast(errMsg(e), 'err'); }
  } else switchWorkspace(pick.id);
}

function sshProbeSuccessToast(r) {
  let msg = `${r.message} · ${r.latencyMs}ms`;
  if (r.fingerprint) {
    msg += `\n${r.fingerprint}`;
    toast(msg, 'ok', 7000);
    const t = $('#toast');
    t.style.cursor = 'pointer';
    t.title = 'انقر لنسخ بصمة السيرفر';
    t.addEventListener('click', () => copyText('بصمة السيرفر', r.fingerprint), { once: true });
    setTimeout(() => { t.style.cursor = ''; t.removeAttribute('title'); }, 7000);
  } else toast(msg, 'ok');
}

const sshFpCacheKey = (host, port) => `me.ssh.fp.${host}:${port}`;

function rememberLastSshConn(conn) {
  if (conn?.id) {
    store('me.ssh.lastConnId', conn.id);
    store('me.ssh.lastConnName', conn.name || '');
    refreshSshLastButton();
  }
}

export function refreshSshLastButton() {
  const btn = $('#btn-ssh-last');
  if (!btn) return;
  const id = load('me.ssh.lastConnId', '');
  btn.hidden = !id;
  const nm = load('me.ssh.lastConnName', '');
  btn.title = nm ? `آخر SSH: ${nm}` : 'آخر اتصال SSH';
  btn.setAttribute('aria-label', btn.title);
}

export async function connectLastSsh() {
  const id = load('me.ssh.lastConnId', '');
  if (!id) { toast('لا يوجد اتصال SSH سابق', 'err'); return; }
  try {
    const list = (await api('/ssh/connections')).connections || [];
    const c = list.find((x) => x.id === id);
    if (!c) { toast('الاتصال السابق غير موجود — اختر من SSH', 'err'); refreshSshLastButton(); return; }
    await sshConnectFlow(c);
  } catch (e) { toast(errMsg(e), 'err'); }
}

async function sshPinHostKeyOnConnection(conn, fingerprint) {
  if (!conn?.id || !fingerprint) return;
  try {
    await api('/ssh/connections/' + conn.id, {
      method: 'PUT',
      body: {
        name: conn.name,
        host: conn.host,
        port: conn.port,
        username: conn.username,
        auth: conn.auth,
        keyId: conn.keyId,
        rootDir: conn.rootDir || '',
        hostKey: fingerprint,
      },
    });
    conn.hostKey = fingerprint;
  } catch { /* optional */ }
}

async function sshEnsureHostTrust(base, { confirmNew = true } = {}) {
  if (base.hostKey && /^SHA256:[A-Za-z0-9+/]{43}$/.test(base.hostKey)) return base.hostKey;
  const cached = load(sshFpCacheKey(base.host, base.port), '');
  if (cached && /^SHA256:/.test(cached)) return cached;
  const { fingerprint } = await api('/ssh/hostkey', { method: 'POST', body: { host: base.host, port: base.port } });
  if (cached && cached !== fingerprint) {
    if (!(await confirmBox(`تحذير: بصمة السيرفر تغيّرت!\n\nكانت:\n${cached}\n\nالآن:\n${fingerprint}`, 'متابعة', true, 'SSH'))) return null;
  } else if (confirmNew) {
    if (!(await confirmBox(`بصمة السيرفر:\n${fingerprint}\n\nهل تثق بهذا السيرفر؟`, 'ثقة', false, 'SSH'))) return null;
  }
  store(sshFpCacheKey(base.host, base.port), fingerprint);
  return fingerprint;
}

async function sshSaveAndConnectFromProbe(base, probeResult) {
  if (!(await confirmBox('حفظ هذا الاتصال والاتصال بمساحة SFTP؟', 'حفظ واتصال', false, 'SSH'))) return;
  try {
    const body = {
      name: base.name || `${base.username}@${base.host}`,
      host: base.host,
      port: base.port,
      username: base.username,
      auth: base.auth,
    };
    if (base.auth === 'key') body.keyId = base.keyId;
    else if (base._password) body.password = base._password;
    if (probeResult.fingerprint) body.hostKey = probeResult.fingerprint;
    const r = await api('/ssh/connections', { method: 'POST', body });
    await sshConnectFlow(r.connection);
  } catch (e) { toast(errMsg(e), 'err'); }
}

async function sshProbeRequest(base, { trust, password } = {}) {
  const body = { ...base };
  delete body._password;
  delete body.name;
  if (base.hostKey) body.hostKey = base.hostKey;
  if (trust) body.trust = trust;
  if (password) {
    body.auth = 'password';
    body.password = password;
    delete body.keyId;
  }
  if (body.auth === 'key' && !body.keyId) {
    throw new Error('اختر مفتاح SSH صالحاً');
  }
  return api('/ssh/probe', { method: 'POST', body });
}

async function sshLoadKeyOptions() {
  try {
    return ((await api('/ssh/keys')).keys || []).map((k) => ({ value: k.id, label: k.name }));
  } catch {
    return [];
  }
}

async function sshProbeFlow(preset = {}, { skipForm = false, offerSaveConnect = false } = {}) {
  let base;
  const keyOptions = preset.keyId ? [] : await sshLoadKeyOptions();
  if (!preset.keyId && !keyOptions.length && preset.auth !== 'password') {
    toast('أضف مفتاحاً من «SSH → مفاتيح SSH» أو اختر مصادقة بكلمة سر', 'err');
    return null;
  }
  const defaultKeyId = preset.keyId || keyOptions[0]?.value || '';

  if (skipForm && preset.host) {
    const auth = preset.auth || (defaultKeyId ? 'key' : 'password');
    base = {
      host: String(preset.host).trim(),
      port: Number(preset.port) || 22,
      username: String(preset.username || 'root').trim() || 'root',
      auth,
      keyId: auth === 'key' ? defaultKeyId : undefined,
      hostKey: preset.hostKey || '',
      name: preset.name,
      _conn: preset._conn,
    };
    if (auth === 'key' && !base.keyId) {
      toast('لا يوجد مفتاح SSH — أضف مفتاحاً أولاً', 'err');
      return null;
    }
  } else {
    const fields = [
      { key: 'host', label: 'المضيف', icon: 'server', value: preset.host || '', mono: true, placeholder: '203.0.113.10' },
      { key: 'port', label: 'المنفذ', icon: 'plug', type: 'number', value: String(preset.port ?? 22) },
      { key: 'username', label: 'المستخدم', icon: 'user', value: preset.username || 'root', mono: true },
    ];
    if (!preset.keyId) {
      fields.push(
        {
          key: 'auth',
          label: 'المصادقة',
          icon: 'key',
          type: 'select',
          value: keyOptions.length ? 'key' : 'password',
          options: [
            { value: 'key', label: 'مفتاح SSH' },
            { value: 'password', label: 'كلمة سر' },
          ],
        },
        {
          key: 'keyId',
          label: 'المفتاح',
          icon: 'key',
          type: 'select',
          value: defaultKeyId,
          options: keyOptions.length ? keyOptions : [{ value: '', label: '— لا مفاتيح —' }],
          showIf: (d) => d.auth === 'key',
        },
      );
    }
    const v = await proFormSheet('اختبار اتصال SSH', fields, { okLabel: 'اختبار', icon: 'check', subtitle: 'بصمة ثم مصادقة' });
    if (!v?.host?.trim()) return null;
    const auth = preset.keyId ? 'key' : (v.auth || (defaultKeyId ? 'key' : 'password'));
    const keyId = preset.keyId || (auth === 'key' ? v.keyId || defaultKeyId : undefined);
    if (auth === 'key' && !keyId) {
      toast('اختر مفتاح SSH', 'err');
      return null;
    }
    base = {
      host: v.host.trim(),
      port: Number(v.port) || 22,
      username: v.username.trim() || 'root',
      auth,
      keyId,
      hostKey: preset.hostKey || '',
      _conn: preset._conn,
    };
  }

  const finishOk = async (r) => {
    if (r.fingerprint) store(sshFpCacheKey(base.host, base.port), r.fingerprint);
    if (base._conn && r.fingerprint) await sshPinHostKeyOnConnection(base._conn, r.fingerprint);
    sshProbeSuccessToast(r);
    if (offerSaveConnect) await sshSaveAndConnectFromProbe(base, r);
    return r;
  };

  const trustFp = await sshEnsureHostTrust(base);
  if (!trustFp) return null;
  base.hostKey = trustFp;

  try {
    return await finishOk(await sshProbeRequest(base, { trust: trustFp }));
  } catch (e) {
    const d = e.data || {};
    if (e.status === 428 && d.code === 'NEED_PASSWORD') {
      const pw = await askText('كلمة سر SSH', '', 'اختبار', { type: 'password' });
      if (!pw) return null;
      base._password = pw;
      try { return await finishOk(await sshProbeRequest(base, { password: pw })); }
      catch (e2) { toast(errMsg(e2), 'err'); return null; }
    }
    toast(errMsg(e), 'err');
    return null;
  }
}

async function sshConnectFlow(conn) {
  const finish = (r) => {
    if (r.ok && r.workspace) {
      rememberLastSshConn(conn);
      toast('متصل: ' + conn.name, 'ok');
      if (!S.workspaces.some((w) => w.id === r.workspace.id)) S.workspaces.push(r.workspace);
      return switchWorkspace(r.workspace.id);
    }
  };
  if (!conn.hostKey) {
    const fp = await sshEnsureHostTrust({ host: conn.host, port: conn.port, hostKey: '' });
    if (!fp) return;
    await sshPinHostKeyOnConnection(conn, fp);
  }
  try {
    return await finish(await api('/ssh/connections/' + conn.id + '/connect', { method: 'POST', body: {} }));
  } catch (e) {
    const d = e.data || {};
    if (e.status === 428 && d.code === 'HOSTKEY_UNKNOWN') {
      const fp = d.fingerprint || (await sshEnsureHostTrust({ host: conn.host, port: conn.port, hostKey: '' }, { confirmNew: false }));
      if (!fp) return;
      await sshPinHostKeyOnConnection(conn, fp);
      try { return await finish(await api('/ssh/connections/' + conn.id + '/connect', { method: 'POST', body: { trust: fp } })); }
      catch (e2) { toast(errMsg(e2), 'err'); return; }
    }
    if (e.status === 428 && d.code === 'NEED_PASSWORD') {
      const pw = await askText('كلمة سر SSH', '', 'اتصال', { type: 'password' });
      if (!pw) return;
      try { return await finish(await api('/ssh/connections/' + conn.id + '/connect', { method: 'POST', body: { password: pw, savePassword: true } })); }
      catch (e2) { toast(errMsg(e2), 'err'); return; }
    }
    toast(errMsg(e), 'err');
  }
}

async function copyText(label, text) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast(`تم نسخ ${label}`, 'ok');
  } catch {
    toast('انسخ يدوياً من الشاشة', 'err');
  }
}

export async function sshKeysSheet() {
  const loadKeys = async () => {
    try { return (await api('/ssh/keys')).keys || []; } catch (e) { toast(errMsg(e), 'err'); return null; }
  };
  let keys = await loadKeys();
  if (keys === null) return;

  for (;;) {
    const a = await proActionSheet({
      title: 'مفاتيح SSH',
      subtitle: 'Ed25519 · استيراد · authorized_keys',
      icon: 'key',
      groups: [
        {
          label: 'إجراءات',
          icon: 'plus',
          actions: [
            { id: 'gen', icon: 'plus', label: 'توليد مفتاح جديد', hint: 'Ed25519 أو RSA' },
            { id: 'import', icon: 'upload', label: 'استيراد مفتاح خاص', hint: 'OpenSSH / PEM' },
          ],
        },
        keys.length ? {
          label: 'مفاتيحك',
          icon: 'key',
          actions: keys.map((k) => ({
            id: 'k:' + k.id,
            icon: 'key',
            label: k.name,
            hint: (k.fingerprint || k.type || '').slice(0, 48),
          })),
        } : null,
      ].filter(Boolean),
    });
    if (!a) return;

    if (a.id === 'gen') {
      const v = await proFormSheet('توليد مفتاح', [
        { key: 'name', label: 'الاسم', icon: 'edit', value: 'مفتاح ed25519' },
        {
          key: 'type',
          label: 'النوع',
          icon: 'key',
          type: 'select',
          value: 'ed25519',
          options: [{ value: 'ed25519', label: 'Ed25519 (موصى به)' }, { value: 'rsa', label: 'RSA' }],
        },
      ], { okLabel: 'توليد', icon: 'key' });
      if (!v) continue;
      try {
        const r = await api('/ssh/keys/generate', { method: 'POST', body: { name: v.name, type: v.type } });
        const pub = r.key?.publicKey || '';
        const keyId = r.key?.id || '';
        await openModal((box, done) => {
          appendSheetProHeader(box, { icon: 'key', title: 'تم إنشاء المفتاح', subtitle: 'انسخ العام إلى authorized_keys' });
          box.append(
            el('p', { class: 'sheet-pro-sub note', text: 'المفتاح الخاص يبقى مشفّراً على السيرفر فقط.' }),
            el('pre', { class: 'mono scroll sheet-pro-pre', text: pub, dir: 'ltr' }),
            el('div', { class: 'sheet-pro-form-btns' },
              el('button', { type: 'button', class: 'ghost', text: 'نسخ العام', onclick: () => copyText('المفتاح العام', pub) }),
              el('button', {
                type: 'button',
                class: 'primary',
                text: 'اختبار الاتصال',
                onclick: async () => { await sshProbeFlow({ keyId }, { offerSaveConnect: true }); },
              }),
              el('button', { type: 'button', class: 'ghost', text: 'تم', onclick: () => done(null) }),
            ),
          );
        }, { cls: 'sheet-pro-glass' });
        keys = await loadKeys() || keys;
      } catch (e) { toast(errMsg(e), 'err'); }
      continue;
    }

    if (a.id === 'import') {
      const v = await proFormSheet('استيراد مفتاح', [
        { key: 'name', label: 'الاسم', icon: 'edit', value: 'مفتاح مستورد' },
        { key: 'privateKey', label: 'المفتاح الخاص', icon: 'key', type: 'textarea', rows: 8, mono: true, placeholder: '-----BEGIN OPENSSH PRIVATE KEY-----' },
        { key: 'passphrase', label: 'Passphrase', icon: 'lock', type: 'password' },
      ], { okLabel: 'استيراد', icon: 'upload' });
      if (!v?.privateKey?.trim()) continue;
      try {
        await api('/ssh/keys/import', {
          method: 'POST',
          body: { name: v.name, privateKey: v.privateKey, passphrase: v.passphrase || undefined },
        });
        toast('تم استيراد المفتاح', 'ok');
        keys = await loadKeys() || keys;
      } catch (e) {
        const d = e.data || {};
        if (d.needPassphrase) toast('المفتاح مشفّر — أدخل passphrase', 'err');
        else toast(errMsg(e), 'err');
      }
      continue;
    }

    if (a.id.startsWith('k:')) {
      const k = keys.find((x) => x.id === a.id.slice(2));
      if (!k) continue;
      const act = await proActionSheet({
        title: k.name,
        subtitle: (k.fingerprint || '').slice(0, 56),
        icon: 'key',
        groups: [{ actions: [
          { id: 'copy', icon: 'copy', label: 'نسخ المفتاح العام' },
          { id: 'del', icon: 'trash', label: 'حذف المفتاح', danger: true },
        ] }],
      });
      if (!act) continue;
      if (act.id === 'copy') await copyText('المفتاح العام', k.publicKey);
      else if (act.id === 'del') {
        if (!(await confirmBox('حذف هذا المفتاح؟ الاتصالات التي تستخدمه ستتوقف.', 'حذف', true, k.name))) continue;
        try {
          await api('/ssh/keys/' + k.id, { method: 'DELETE' });
          toast('تم الحذف', 'ok');
          keys = await loadKeys() || [];
        } catch (e) { toast(errMsg(e), 'err'); }
      }
    }
  }
}

export async function sshSheet() {
  for (;;) {
    let list = [];
    try { list = (await api('/ssh/connections')).connections; } catch (e) { toast(errMsg(e), 'err'); return; }
    const lastId = load('me.ssh.lastConnId', '');
    const a = await proActionSheet({
      title: 'SSH',
      subtitle: 'SFTP · طرفية · Git على السيرفر',
      icon: 'plug',
      groups: [
        {
          label: 'سريع',
          icon: 'check',
          actions: [
            { id: 'add', icon: 'plus', label: 'اتصال جديد', hint: 'حفظ host · مفتاح أو كلمة سر' },
            { id: 'probe', icon: 'check', label: 'اختبار اتصال', hint: 'بصمة · مصادقة · حفظ' },
            { id: 'keys', icon: 'key', label: 'مفاتيح SSH', hint: 'توليد · استيراد · نسخ عام' },
          ],
        },
        list.length ? {
          label: 'اتصالات محفوظة',
          icon: 'server',
          actions: list.map((c) => ({
            id: 'c:' + c.id,
            icon: c.connected ? 'check' : 'server',
            label: c.name,
            hint: `${c.username}@${c.host}:${c.port}${c.hostKey ? ' · بصمة ✓' : ''}`,
            badge: c.id === lastId ? 'آخر' : undefined,
            active: S.w === 'ssh:' + c.id,
          })),
        } : null,
      ].filter(Boolean),
    });
    if (!a) return;
    if (a.id === 'probe') {
      await sshProbeFlow({}, { offerSaveConnect: true });
      continue;
    }
    if (a.id === 'add') {
      let keyOptions = [];
      try {
        keyOptions = ((await api('/ssh/keys')).keys || []).map((k) => ({ value: k.id, label: k.name }));
      } catch { /* */ }
      const v = await proFormSheet('اتصال SSH', [
        { key: 'name', label: 'الاسم', icon: 'edit', value: '' },
        { key: 'host', label: 'المضيف', icon: 'server', value: '', mono: true },
        { key: 'port', label: 'المنفذ', icon: 'plug', type: 'number', value: '22' },
        { key: 'username', label: 'المستخدم', icon: 'user', value: 'root', mono: true },
        { key: 'auth', label: 'المصادقة', icon: 'key', type: 'select', value: 'password', options: [{ value: 'password', label: 'كلمة سر' }, { value: 'key', label: 'مفتاح' }] },
        { key: 'password', label: 'كلمة السر', icon: 'lock', type: 'password', showIf: (d) => d.auth === 'password' },
        {
          key: 'keyId',
          label: 'المفتاح',
          icon: 'key',
          type: 'select',
          value: keyOptions[0]?.value || '',
          options: keyOptions.length ? keyOptions : [{ value: '', label: '— أضف مفتاحاً —' }],
          showIf: (d) => d.auth === 'key',
        },
      ], { okLabel: 'حفظ', icon: 'plug' });
      if (!v || !v.host) continue;
      try {
        const body = { name: v.name, host: v.host, port: Number(v.port) || 22, username: v.username, auth: v.auth };
        if (v.auth === 'password') body.password = v.password;
        else body.keyId = v.keyId;
        const r = await api('/ssh/connections', { method: 'POST', body });
        await sshConnectFlow(r.connection);
        return;
      } catch (e) { toast(errMsg(e), 'err'); }
      continue;
    }
    if (a.id === 'keys') {
      await sshKeysSheet();
      continue;
    }
    if (a.id.startsWith('c:')) {
      const c = list.find((x) => x.id === a.id.slice(2));
      if (!c) continue;
      const sub = await proActionSheet({
        title: c.name,
        subtitle: `${c.username}@${c.host}:${c.port}`,
        icon: 'server',
        groups: [{ actions: [
          { id: 'connect', icon: 'plug', label: 'اتصال SFTP', hint: 'مستكشف · محرر · طرفية' },
          { id: 'test', icon: 'check', label: 'اختبار', hint: 'مصادقة دون فتح جلسة' },
          { id: 'del', icon: 'trash', label: 'حذف الاتصال', danger: true },
        ] }],
      });
      if (!sub) continue;
      if (sub.id === 'connect') {
        await sshConnectFlow(c);
        return;
      }
      if (sub.id === 'test') {
        await sshProbeFlow({
          name: c.name,
          host: c.host,
          port: c.port,
          username: c.username,
          auth: c.auth,
          keyId: c.keyId,
          hostKey: c.hostKey || '',
          _conn: c,
        }, { skipForm: true });
        continue;
      }
      if (sub.id === 'del') {
        if (!(await confirmBox('حذف هذا الاتصال؟', 'حذف', true, c.name))) continue;
        try {
          await api('/ssh/connections/' + c.id, { method: 'DELETE' });
          toast('تم حذف الاتصال', 'ok');
        } catch (e) { toast(errMsg(e), 'err'); }
      }
    }
  }
}

export async function searchInFiles() {
  const q = await askText('بحث داخل الملفات (rg)', '', 'بحث');
  if (!q) return;
  try {
    const r = await apiw('/search', { method: 'POST', body: { q, max: 80 } });
    if (!r.results?.length) { toast('لا نتائج'); return; }
    await openModal((box, done) => {
      box.append(el('h3', { text: `نتائج (${r.results.length}) · ${r.engine || 'rg'}` }));
      for (const hit of r.results) {
        box.append(el('button', {
          class: 'listbtn mono',
          type: 'button',
          onclick: () => { done(hit); },
        }, el('span', { text: `${hit.path}:${hit.line}`, dir: 'ltr' }), el('small', { text: hit.text?.slice(0, 120) || '' })));
      }
      box.append(el('div', { class: 'btns' }, el('button', { text: 'إغلاق', onclick: () => done(null) })));
    }, { full: true }).then((hit) => {
      if (hit) import('./editor.js').then(({ openFile, goto }) => openFile(S.w, hit.path).then(() => goto(hit.line)));
    });
  } catch (e) { toast(errMsg(e), 'err'); }
}

export async function findFileByName() {
  const q = await askText('ابحث عن ملف بالاسم', '', 'بحث');
  if (!q) return;
  try {
    const { results } = await apiw('/find', { query: { q } });
    if (!results.length) { toast('لا نتائج'); return; }
    const pick = await openModal((box, done) => {
      box.append(el('h3', { text: `النتائج (${results.length})` }));
      for (const r of results) box.append(el('button', { class: 'listbtn mono', type: 'button', onclick: () => done(r) }, r));
      box.append(el('div', { class: 'btns' }, el('button', { text: 'إغلاق', onclick: () => done(null) })));
    });
    if (pick) import('./editor.js').then(({ openFile }) => openFile(S.w, pick));
  } catch (e) { toast(errMsg(e), 'err'); }
}

function billingMeterEl(st) {
  const used = st.omdaaTokensUsed || 0;
  const limit = st.omdaaTokensLimit || 1;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const warn = pct >= 80;
  const fmt = (n) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(n));
  return el('div', { class: 'billing-card billing-card-pro' + (warn ? ' billing-warn' : ''), role: 'status', 'aria-live': 'polite' },
    el('div', { class: 'billing-card-head' },
      el('span', { class: 'billing-card-ico', 'aria-hidden': 'true' }, ic('sparkles', 20, 1.75)),
      el('span', { class: 'billing-plan-badge', text: st.planLabel || 'Free' }),
      el('span', { class: 'billing-pct', text: `${pct}%`, 'aria-hidden': 'true' }),
    ),
    el('p', { class: 'billing-usage', id: 'billing-usage-text' },
      `رصيد Omdaa: ${fmt(used)} / ${fmt(limit)} token`,
    ),
    el('div', { class: 'billing-meter', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(pct), 'aria-labelledby': 'billing-usage-text' },
      el('div', { class: 'billing-meter-fill', style: `width:${pct}%` }),
    ),
    warn ? el('p', { class: 'billing-hint-warn', text: 'اقتربت من حد الرصيد — BYOK أو ترقية Pro.' }) : null,
    st.hasByok ? el('p', { class: 'billing-hint', text: '✓ مفتاح BYOK مُعد — يُستخدم حسب وضع المصدر.' }) : el('p', { class: 'billing-hint', text: 'أضف مزوّداً من «الذكاء الاصطناعي (BYOK)» لتجاوز الرصيد.' }),
  );
}

function proToggleRow(icon, labelId, label, switchBtn) {
  return el('div', { class: 'sheet-pro-toggle' },
    el('span', { class: 'sheet-pro-toggle-ico', 'aria-hidden': 'true' }, ic(icon, 18, 1.75)),
    el('span', { id: labelId, class: 'sheet-pro-toggle-label', text: label }),
    switchBtn);
}

async function setAiSource(id) {
  try {
    S.billing = await api('/billing/prefs', { method: 'PUT', body: { aiSource: id } });
    await refreshAiReady();
    toast('تم تحديث مصدر Ai', 'ok');
    return true;
  } catch (e) { toast(errMsg(e), 'err'); return false; }
}

export async function billingSheet() {
  let st = S.billing;
  try { st = await api('/billing/status'); S.billing = st; } catch (e) { toast(errMsg(e), 'err'); return; }
  await openModal((box, done) => {
    const src = st.aiSource || 'auto';
    const srcBtn = (id, label) => el('button', {
      type: 'button',
      class: 'listbtn' + (src === id ? ' on' : ''),
      'aria-pressed': String(src === id),
      onclick: async () => {
        if (await setAiSource(id)) { done(null); billingSheet(); }
      },
    }, label);

    box.append(
      el('h3', { id: 'billing-modal-title', text: 'Omdaa AI · الاشتراك' }),
      billingMeterEl(st),
      el('p', { class: 'billing-section-label', text: 'مصدر الذكاء الاصطناعي' }),
      srcBtn('auto', 'تلقائي — BYOK ثم رصيد Omdaa'),
      srcBtn('byok', 'BYOK فقط (مفتاحك)'),
      srcBtn('omdaa', 'رصيد Omdaa فقط'),
      el('div', { class: 'btns col' },
        st.stripeConfigured ? el('button', {
          type: 'button',
          class: 'primary',
          text: st.plan === 'pro' ? 'إدارة Pro (Stripe)' : 'ترقية Pro — 2M token/شهر',
          onclick: async () => {
            try {
              const { url } = await api('/billing/checkout', { method: 'POST', body: {} });
              if (url) window.location.href = url;
            } catch (e) { toast(errMsg(e), 'err'); }
          },
        }) : null,
        el('button', { type: 'button', text: 'إغلاق', onclick: () => done(null) }),
      ),
    );
  });
}

export async function aiSettingsSheet() {
  await refreshAiReady();
  await refreshOpencode();
  const c = S.aiConfig || (await api('/ai/config'));
  const ocItems = S.features?.opencode ? [
    { id: 'oc-run', label: 'تشغيل OpenCode في الطرفية', icon: 'terminal' },
    { id: 'oc-models', label: 'نماذج مجانية + تلقائي', hint: 'تدوير عند الضغط', icon: 'list' },
    { id: 'oc-stats', label: 'لوحة استخدام النماذج', hint: 'نجاح · فشل · تدوير', icon: 'history' },
    { sep: true },
  ] : [];
  const a = await sheet('الذكاء الاصطناعي', [
    ...ocItems,
    { id: 'add', label: 'إضافة مزوّد Omdaa', icon: 'plus' },
    ...c.providers.map((p) => ({ id: 'p:' + p.id, label: p.name, hint: p.hasKey ? 'مفتاح محفوظ' : 'بدون مفتاح', icon: 'sparkles' })),
    { sep: true },
    { id: 'inline', label: S.settings.inlineAi ? '✓ إكمال مضمّن مفعّل' : 'تفعيل الإكمال المضمّن', icon: 'wand' },
  ]);
  if (!a) return;
  if (a.id === 'oc-run') { launchOpencode(); return; }
  if (a.id === 'oc-models') { showFreeModels(); return; }
  if (a.id === 'oc-stats') { showModelStats(); return; }
  if (a.id === 'inline') {
    S.settings.inlineAi = !S.settings.inlineAi;
    saveSettings();
    toast(S.settings.inlineAi ? 'إكمال مضمّن ON' : 'OFF', 'ok');
    return;
  }
  if (a.id === 'add') {
    const v = await formSheet('مزوّد AI', [
      { key: 'name', label: 'الاسم', value: 'OpenAI' },
      { key: 'type', label: 'النوع', type: 'select', value: 'openai', options: [{ value: 'openai', label: 'OpenAI-compatible' }, { value: 'anthropic', label: 'Anthropic' }] },
      { key: 'baseUrl', label: 'Base URL', value: 'https://api.openai.com/v1', mono: true },
      { key: 'apiKey', label: 'API Key', type: 'password', mono: true },
      { key: 'models', label: 'نماذج (فاصلة)', value: 'gpt-4o-mini', mono: true },
    ]);
    if (!v?.baseUrl) return;
    try {
      const r = await api('/ai/providers', { method: 'POST', body: { ...v, models: String(v.models).split(',').map((s) => s.trim()) } });
      await api('/ai/settings', { method: 'PUT', body: { model: `${r.provider.id}:${String(v.models).split(',')[0].trim()}` } });
      await refreshAiReady();
      toast('تم الحفظ', 'ok');
    } catch (e) { toast(errMsg(e), 'err'); }
  } else if (a.id.startsWith('p:')) {
    const pid = a.id.slice(2);
    const p = c.providers.find((x) => x.id === pid);
    const model = p?.models[0] || 'default';
    try {
      await api('/ai/settings', { method: 'PUT', body: { model: `${pid}:${model}` } });
      await refreshAiReady();
      toast('تم اختيار ' + p.name, 'ok');
    } catch (e) { toast(errMsg(e), 'err'); }
  }
}

export async function mcpServersSheet() {
  let servers = [];
  let presets = { presets: [], embeddings: {} };
  try {
    servers = (await api('/mcp/servers')).servers || [];
    presets = await api('/mcp/presets');
  } catch (e) {
    toast(errMsg(e), 'err');
    return;
  }
  await proActionSheet({
    title: 'خوادم MCP',
    subtitle: 'أدوات MCP · Context7 · GitHub · Memory',
    icon: 'server',
    groups: [
      {
        label: 'قوالب جاهزة',
        icon: 'sparkles',
        actions: (presets.presets || []).map((p) => ({
          id: `preset:${p.id}`,
          label: p.name,
          hint: p.hint + (p.note ? ` · ${p.note}` : ''),
          icon: 'github',
        })),
      },
      {
        label: 'الخوادم',
        icon: 'list',
        actions: servers.length
          ? servers.map((s) => ({
            id: `srv:${s.id}`,
            label: `${s.name} (${s.type})`,
            hint: s.enabled ? (s.trusted ? 'موثوق · مفعّل' : 'مفعّل') : 'معطّل',
            icon: 'terminal',
          }))
          : [{ id: 'empty', label: 'لا خوادم بعد', hint: 'ثبّت GitHub من القوالب (PAT في Git)', icon: 'info' }],
      },
      {
        actions: [
          { id: 'test-all', label: 'اختبار الخوادم', icon: 'check', hint: 'اتصال + tools/list' },
          { id: 'embed-info', label: `بحث دلالي: ${presets.embeddings?.enabled ? 'مفعّل' : 'غير متاح'}`, icon: 'search', hint: presets.embeddings?.model || 'Groq embeddings' },
        ],
      },
    ],
    footLabel: 'إغلاق',
  }).then(async (pick) => {
    if (!pick) return;
    if (pick.id.startsWith('preset:')) {
      const pid = pick.id.slice(7);
      try {
        await api(`/mcp/presets/${encodeURIComponent(pid)}/install`, { method: 'POST', body: {} });
        toast('✓ تم تثبيت قالب MCP', 'ok', 3500);
        void mcpServersSheet();
      } catch (e) {
        toast(errMsg(e), 'err', 5500);
      }
      return;
    }
    if (pick.id.startsWith('srv:')) {
      const sid = pick.id.slice(4);
      try {
        const r = await api(`/mcp/servers/${encodeURIComponent(sid)}/test`, { method: 'POST', body: {} });
        toast(`✓ ${r.tools?.length || 0} أداة`, 'ok', 3000);
      } catch (e) { toast(errMsg(e), 'err'); }
    }
    if (pick.id === 'empty') {
      toast('اختر «GitHub (MCP رسمي)» من القوالب — بعد حفظ PAT', 'ok', 4000);
      return;
    }
    if (pick.id === 'test-all') {
      for (const s of servers) {
        try {
          await api(`/mcp/servers/${encodeURIComponent(s.id)}/test`, { method: 'POST', body: {} });
        } catch { /* skip */ }
      }
      toast('انتهى الاختبار', 'ok');
    }
  });
}

export async function adminUsersSheet() {
  if (S.user?.role !== 'admin') return;
  let users = [];
  try { users = (await api('/admin/users')).users; } catch (e) { toast(errMsg(e), 'err'); return; }
  await openModal((box, done) => {
    const draw = () => {
      box.replaceChildren(el('div', { class: 'grab' }), el('h3', { text: 'المستخدمون' }));
      for (const u of users) {
        box.append(el('div', { class: 'trow' }, el('b', { text: u.username }), el('small', { text: u.role + (u.disabled ? ' · معطّل' : '') })));
      }
      box.append(el('div', { class: 'btns' },
        el('button', { class: 'primary', text: 'مستخدم جديد', onclick: async () => {
          const v = await formSheet('مستخدم', [{ key: 'username', label: 'اسم المستخدم' }, { key: 'password', label: 'كلمة السر', type: 'password' }]);
          if (!v?.username) return;
          try { await api('/admin/users', { method: 'POST', body: v }); users = (await api('/admin/users')).users; draw(); toast('تم', 'ok'); } catch (e) { toast(errMsg(e), 'err'); }
        } }),
        el('button', { text: 'إغلاق', onclick: () => done(null) }),
      ));
    };
    draw();
  }, { full: true });
}

function fmtDeviceWhen(ts) {
  if (!ts) return '—';
  try {
    return new Intl.DateTimeFormat('ar', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Riyadh' }).format(new Date(ts));
  } catch {
    return new Date(ts).toLocaleString('ar');
  }
}

/** قائمة الأجهزة / الشبكات المعروفة — حذف · تسمية */
export async function devicesSheet() {
  const u = S.user;
  if (!u || u.builtin) { toast('غير متاح لحساب المدير', 'err'); return; }

  const draw = async () => {
    let devices = [];
    try {
      devices = (await api('/account/devices')).devices || [];
    } catch (e) {
      toast(errMsg(e), 'err');
      return;
    }

    const picked = await openModal((box, done) => {
      appendSheetProHeader(box, {
        icon: 'shield',
        title: 'الأجهزة المعروفة',
        subtitle: 'شبكات الدخول المألوفة · /24',
      });
      appendSheetProSection(box, 'الشبكات المحفوظة', 'list');
      if (!devices.length) {
        box.append(el('p', {
          class: 'sheet-pro-row-hint',
          text: 'لا أجهزة بعد. عند أول دخول من شبكة جديدة تُحفظ هنا تلقائياً.',
          style: 'padding:12px 16px;margin:0;color:var(--muted,#94a3b8)',
        }));
      }
      for (const d of devices) {
        const title = d.label || d.device || 'جهاز';
        const loc = d.location || [d.city, d.country].filter(Boolean).join('، ');
        const hint = [
          loc || null,
          d.ip,
          d.net && d.net !== d.ip ? d.net : null,
          `آخر ظهور ${fmtDeviceWhen(d.lastSeen)}`,
        ].filter(Boolean).join(' · ');
        box.append(sheetProRow({
          icon: 'shield',
          label: title,
          hint,
          badge: d.countryCode || d.via || undefined,
          onclick: () => done(d),
        }));
      }
      appendSheetProFoot(box, done);
    }, { cls: 'sheet-pro-glass' });

    if (!picked) return;

    const locLine = picked.location || [picked.city, picked.country].filter(Boolean).join('، ');
    const act = await proActionSheet({
      icon: 'shield',
      title: picked.label || picked.device || 'جهاز',
      subtitle: [locLine, picked.ip, picked.net].filter(Boolean).join(' · '),
      groups: [{
        label: 'إجراءات',
        icon: 'list',
        actions: [
          { id: 'rename', label: 'تسمية الجهاز', icon: 'edit', hint: 'مثل: بيت · جوال · مكتب' },
          { id: 'delete', label: 'حذف من المألوف', icon: 'trash', hint: 'الدخول التالي من هذه الشبكة يُرسل تنبيهاً', danger: true },
        ],
      }],
    });
    if (!act) { void draw(); return; }

    if (act.id === 'rename') {
      const v = await proFormSheet('تسمية الجهاز', [
        { key: 'label', label: 'الاسم', icon: 'edit', value: picked.label || '', placeholder: 'مثال: جوال البيت' },
      ], { okLabel: 'حفظ', icon: 'edit', subtitle: picked.ip });
      if (v && v.label !== undefined) {
        try {
          await api(`/account/devices/${encodeURIComponent(picked.id)}`, { method: 'PATCH', body: { label: v.label } });
          toast('تم حفظ الاسم', 'ok');
        } catch (e) { toast(errMsg(e), 'err'); }
      }
      return draw();
    }

    if (act.id === 'delete') {
      if (!(await confirmBox(
        'حذف هذه الشبكة؟',
        'أي دخول لاحق من نفس الشبكة (/24) سيُعتبر جديداً ويُرسل بريداً.',
        'حذف',
        true,
      ))) return draw();
      try {
        await api(`/account/devices/${encodeURIComponent(picked.id)}`, { method: 'DELETE' });
        toast('تم الحذف', 'ok');
      } catch (e) { toast(errMsg(e), 'err'); }
      return draw();
    }
  };

  return draw();
}

export async function accountSheet() {
  const u = S.user;
  if (!u || u.builtin) { toast('حساب المدير لا يستخدم البريد هنا', 'err'); return; }
  const email = u.email || '—';
  const verified = u.emailVerified ? '✓ موثّق' : 'غير موثّق (اختياري)';
  const a = await sheet('حسابي', [
    { id: 'info', label: email, hint: verified, icon: 'file' },
    ...(u.emailVerified ? [] : [{ id: 'verify', label: 'إرسال رابط توثيق البريد', icon: 'sparkles', hint: 'اختياري — من الإعدادات' }]),
    { id: 'devices', label: 'الأجهزة المعروفة', icon: 'shield', hint: 'IP · شبكة /24 · تسمية · حذف' },
    { id: 'pass', label: 'تغيير كلمة السر', icon: 'key' },
    { id: 'logoutAll', label: 'قطع كل الجلسات', icon: 'x', hint: 'يسجّلك خروجاً من كل الأجهزة' },
  ]);
  if (!a) return;
  if (a.id === 'verify') {
    try {
      const r = await api('/account/verify/send', { method: 'POST', body: {} });
      toast(r.sent ? 'تحقق من بريدك' : 'البريد غير مُرسِل — فعّل RESEND_API_KEY على السيرفر', r.sent ? 'ok' : 'err');
    } catch (e) { toast(errMsg(e), 'err'); }
    return;
  }
  if (a.id === 'devices') {
    return devicesSheet();
  }
  if (a.id === 'logoutAll') {
    if (!(await confirmBox('قطع كل الجلسات؟', 'ستحتاج لتسجيل الدخول مجدداً على كل الأجهزة.', 'قطع', true))) return;
    try {
      await api('/logout-all', { method: 'POST' });
      toast('تم قطع الجلسات', 'ok');
      setTimeout(() => location.reload(), 500);
    } catch (e) { toast(errMsg(e), 'err'); }
    return;
  }
  if (a.id === 'pass') {
    const v = await formSheet('كلمة سر جديدة', [
      { key: 'current', label: 'الحالية', type: 'password' },
      { key: 'next', label: 'الجديدة (8+)', type: 'password' },
    ]);
    if (!v?.next) return;
    try {
      await api('/password', { method: 'POST', body: { current: v.current, next: v.next } });
      toast('تم تغيير كلمة السر', 'ok');
    } catch (e) { toast(errMsg(e), 'err'); }
  }
}

export function settingsSheet() {
  return openModal(async (box, done) => {
    let billSt = S.billing;
    try { billSt = await api('/billing/status'); S.billing = billSt; } catch { /* */ }

    const fontVal = el('b', { id: 'set-font-val', text: S.settings.font + 'px' });
    const step = (d) => {
      S.settings.font = Math.min(26, Math.max(10, S.settings.font + d));
      fontVal.textContent = S.settings.font + 'px';
      saveSettings();
      document.documentElement.style.setProperty('--fs', S.settings.font + 'px');
      if (E.view) E.view.requestMeasure();
      fitActiveTerm();
      emit('font');
    };
    const sw = (key, labelId, onChange) => {
      const b = el('button', {
        class: 'switch' + (S.settings[key] ? ' on' : ''),
        role: 'switch',
        type: 'button',
        id: `set-sw-${key}`,
        'aria-checked': String(!!S.settings[key]),
        'aria-labelledby': labelId,
      });
      b.addEventListener('click', () => {
        S.settings[key] = !S.settings[key];
        b.classList.toggle('on', S.settings[key]);
        b.setAttribute('aria-checked', String(S.settings[key]));
        saveSettings();
        if (onChange) onChange();
      });
      return b;
    };

    const homePick = load('me.homeView', 'editor');
    const homeSel = el('select', { id: 'set-home-view', class: 'sheet-pro-select', 'aria-label': 'الشاشة الافتراضية عند الدخول' });
    for (const v of HOME_VIEW_OPTIONS) {
      homeSel.append(el('option', { value: v.id, text: v.label, selected: v.id === homePick }));
    }
    homeSel.addEventListener('change', () => {
      store('me.homeView', homeSel.value);
      toast('ستُفتح «' + (HOME_VIEW_OPTIONS.find((x) => x.id === homeSel.value)?.label || homeSel.value) + '» في الدخول القادم', 'ok', 2200);
    });

    const mobileSplitSw = el('button', {
      class: 'switch' + (readMobileAutoSplitAgent() ? ' on' : ''),
      role: 'switch',
      type: 'button',
      id: 'set-sw-mobile-split',
      'aria-checked': String(readMobileAutoSplitAgent()),
      'aria-labelledby': 'set-label-mobile-split',
    });
    mobileSplitSw.addEventListener('click', () => {
      const on = !readMobileAutoSplitAgent();
      persistMobileAutoSplitAgent(on);
      mobileSplitSw.classList.toggle('on', on);
      mobileSplitSw.setAttribute('aria-checked', String(on));
      toast(on ? 'سيُفتح split عند إرسال للوكيل' : 'split يدوي فقط', 'ok', 2000);
    });

    appendSheetProHeader(box, {
      icon: 'settings',
      title: 'الإعدادات',
      subtitle: 'المحرر · Ai · المشروع · الحساب',
    });
    if (billSt) {
      appendSheetProSection(box, 'Omdaa Ai والرصيد', 'sparkles');
      box.append(el('div', { class: 'set-billing-wrap' },
        billingMeterEl(billSt),
        sheetProRow({
          icon: 'cloud',
          label: 'تفاصيل الاشتراك ومصدر Ai',
          hint: 'Pro · BYOK · تلقائي',
          onclick: () => { done(null); billingSheet(); },
        })));
    }
    appendSheetProSection(box, 'تجربة الدخول', 'play');
    box.append(
      el('div', { class: 'sheet-pro-field' },
        el('span', { class: 'sheet-pro-field-ico', 'aria-hidden': 'true' }, ic('code', 18)),
        el('label', { class: 'sheet-pro-field-body', for: 'set-home-view' },
          el('span', { id: 'set-label-home', class: 'sheet-pro-field-label', text: 'الشاشة الافتراضية' }),
          homeSel)),
      el('div', { class: 'sheet-pro-field' },
        el('span', { class: 'sheet-pro-field-ico', 'aria-hidden': 'true' }, ic('edit', 18)),
        el('div', { class: 'sheet-pro-field-body' },
          el('span', { id: 'set-label-font', class: 'sheet-pro-field-label', text: 'حجم الخط' }),
          el('div', { class: 'stepper sheet-pro-stepper', role: 'group', 'aria-labelledby': 'set-label-font' },
            el('button', { type: 'button', text: '−', 'aria-label': 'تصغير الخط', onclick: () => step(-1) }),
            fontVal,
            el('button', { type: 'button', text: '+', 'aria-label': 'تكبير الخط', onclick: () => step(1) })))),
    );
    appendSheetProSection(box, 'المحرر', 'code');
    box.append(
      proToggleRow('list', 'set-label-wrap', 'لف الأسطر', sw('wrap', 'set-label-wrap', () => applyPrefs())),
      proToggleRow('save', 'set-label-autosave', 'حفظ تلقائي', sw('autosave', 'set-label-autosave')),
      proToggleRow('sparkles', 'set-label-inlineAi', 'إكمال AI مضمّن', sw('inlineAi', 'set-label-inlineAi')),
      proToggleRow('check', 'set-label-deskLintApi', 'فحص lint في المحرر', sw('deskLintApi', 'set-label-deskLintApi', () => import('./editor.js').then((m) => m.refreshDeskLint()))),
      proToggleRow('keyboard', 'set-label-mobile-split', 'split Ai تلقائي (موبايل)', mobileSplitSw),
    );
    appendSheetProSection(box, 'المشروع والملفات', 'folder');
    box.append(
      sheetProRow({ icon: 'server', label: 'مساحة العمل / SSH', hint: 'تسمية · sandbox · hosts', onclick: () => { done(null); workspaceSheet(); } }),
      sheetProRow({ icon: 'search', label: 'بحث في الملفات', hint: 'ripgrep داخل المشروع', onclick: () => { done(null); searchInFiles(); } }),
      sheetProRow({ icon: 'trash', label: 'سلة المحذوفات', hint: 'استرجاع خلال 30 يوماً', onclick: () => { done(null); trashSheet(); } }),
      sheetProRow({ icon: 'upload', label: 'رفع ملفات', hint: 'ZIP أو ملفات متعددة', onclick: () => { done(null); pickUpload(); } }),
      sheetProRow({ icon: 'save', label: 'حفظ كل الملفات', hint: 'كل التبويبات المفتوحة', onclick: async () => { for (const t of visibleTabs()) if (t.dirty) await saveTab(t); toast('تم', 'ok'); } }),
    );
    appendSheetProSection(box, 'الحساب والذكاء', 'user');
    box.append(
      sheetProRow({ icon: 'user', label: 'حسابي · البريد', hint: 'التوثيق · كلمة السر', onclick: () => { done(null); accountSheet(); } }),
      sheetProRow({ icon: 'shield', label: 'الأجهزة المعروفة', hint: 'IP · شبكة /24 · تسمية · حذف', onclick: () => { done(null); devicesSheet(); } }),
      sheetProRow({ icon: 'sparkles', label: 'Omdaa AI · الاشتراك', hint: 'Pro · Stripe · الرصيد', onclick: () => { done(null); billingSheet(); } }),
      sheetProRow({ icon: 'key', label: 'الذكاء الاصطناعي (BYOK)', hint: 'Groq · OpenAI · OpenRouter', onclick: () => { done(null); aiSettingsSheet(); } }),
      sheetProRow({ icon: 'sparkles', label: 'معاينة الموقع (/live)', hint: 'عرض الصفحات الثابتة', onclick: () => { done(null); showLivePreviewSheet(); } }),
      sheetProRow({ icon: 'server', label: 'خوادم MCP · GitHub', hint: 'قالب رسمي + PAT', onclick: () => { done(null); mcpServersSheet(); } }),
      sheetProRow({ icon: 'list', label: 'قواعد المشروع', hint: '.cursorrules · AGENTS.md', onclick: () => { done(null); openRulesEditor(); } }),
      S.user?.role === 'admin' ? sheetProRow({ icon: 'shield', label: 'لوحة الإدارة المتكاملة', hint: 'مستخدمون · بث حي · محادثات', onclick: () => { done(null); import('./admin-console.js').then((m) => m.openAdminConsole()); } }) : null,
      sheetProRow({
        icon: 'trash',
        label: 'مسح بيانات الجهاز',
        hint: 'محادثات وتبويبات محلية · لا يحذف السيرفر',
        danger: true,
        onclick: async () => {
          done(null);
          if (!(await confirmBox(
            'مسح كل بيانات هذا الجهاز؟',
            'يُحذف التخزين المحلي لهذا الحساب. المحادثات المحفوظة على السيرفر تُعاد عند إعادة الفتح.',
            'مسح',
            true,
          ))) return;
          try {
            const n = clearDeviceLocalData({ includeGuest: true });
            toast(`تم مسح ${n} مفتاحاً محلياً — جارٍ إعادة التحميل`, 'ok', 2500);
            setTimeout(() => location.reload(), 600);
          } catch (ex) {
            toast(errMsg(ex), 'err');
          }
        },
      }),
      sheetProRow({ icon: 'x', label: 'تسجيل الخروج', danger: true, onclick: async () => { done(null); try { await api('/logout', { method: 'POST' }); } catch { /* */ } location.reload(); } }),
    );
    appendSheetProFoot(box, done);
  }, { cls: 'sheet-pro-glass' });
}
